import {useCallback, useEffect, useState} from "react"

import type {TOptions} from "i18next"
import {useTranslation} from "react-i18next"

import {
    applyLanguagePreference,
    i18n,
    readLanguagePreference,
    resolveLocale,
    type LanguagePreference,
    type SupportedLocale,
} from "./i18n"
import type en from "./locales/en"

type TranslationKey = keyof typeof en
/** A plural family's base key; i18next picks `_one` / `_other` from `count` (LibreChat). */
type PluralBaseKey<K> = K extends `${infer Base}_other` ? Base : never
export type TranslationKeys = TranslationKey | PluralBaseKey<TranslationKey>

/**
 * `localize("com_ui_save")` — typed against the English catalog, re-renders on language change.
 * Bound to the Agenta instance directly, so it works without a provider (e.g. in web/oss).
 */
export function useLocalize() {
    const {t} = useTranslation("translation", {i18n})
    return useCallback((key: TranslationKeys, options?: TOptions) => t(key, options) as string, [t])
}

/** The active locale, for `Intl` formatting. */
export function useLocale(): SupportedLocale {
    const {i18n: instance} = useTranslation("translation", {i18n})
    return (instance.resolvedLanguage ?? "en") as SupportedLocale
}

/** Apply the stored preference once per app load. Mount at the app root. */
export function useInitLanguage() {
    useEffect(() => {
        void applyLanguagePreference(readLanguagePreference(), {persist: false})
    }, [])
}

/** The picker's state: the stored preference (incl. `auto`) and a setter that applies it. */
export function useLanguagePreference() {
    const [preference, setPreference] = useState<LanguagePreference>(() =>
        typeof window === "undefined" ? "auto" : readLanguagePreference(),
    )
    const select = useCallback((next: LanguagePreference) => {
        setPreference(next)
        void applyLanguagePreference(next)
    }, [])
    return {preference, resolved: resolveLocale(preference), select}
}

/** Renders nothing; mounting it at an app root applies the stored language once. */
export function LanguageSync() {
    useInitLanguage()
    return null
}
